import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  fetchCarouselImages,
  createCarouselImage,
  deleteCarouselImage,
} from '../../features/carousel/carouselSlice';

export default function BannerManager() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { images, status, error } = useSelector((state) => state.carousel);
  const [openDialog, setOpenDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchCarouselImages());
  }, [dispatch]);

  const handleAddNew = () => {
    setImageUrl('');
    setAltText('');
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (!imageUrl) {
      setSnackbar({
        open: true,
        message: t('admin.banners.enterUrl'),
        severity: 'error',
      });
      return;
    }

    try {
      const formattedUrl = imageUrl.startsWith('http') ? imageUrl : `https://${imageUrl}`;
      
      await dispatch(createCarouselImage({
        image_url: formattedUrl,
        alt_text: altText
      })).unwrap();
      
      setOpenDialog(false);
      setImageUrl('');
      setAltText('');
      setSnackbar({
        open: true,
        message: t('admin.banners.addSuccess'),
        severity: 'success',
      });
      dispatch(fetchCarouselImages());
    } catch (error) {
      let errorMessage = t('admin.banners.addError');
      if (error.detail?.[0]?.msg) {
        errorMessage = error.detail[0].msg;
      } else if (typeof error.detail === 'string') {
        errorMessage = error.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleDeleteClick = (banner) => {
    setBannerToDelete(banner);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) return;

    try {
      await dispatch(deleteCarouselImage(bannerToDelete.id)).unwrap();
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
      setSnackbar({
        open: true,
        message: t('admin.banners.deleteSuccess'),
        severity: 'success',
      });
      dispatch(fetchCarouselImages());
    } catch (error) {
      setSnackbar({
        open: true,
        message: t('admin.banners.deleteError'),
        severity: 'error',
      });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('admin.banners.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          {t('admin.banners.addNew')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.banners.preview')}</TableCell>
              <TableCell>{t('admin.banners.url')}</TableCell>
              <TableCell>{t('admin.banners.altText')}</TableCell>
              <TableCell>{t('admin.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === 'loading' ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : images.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t('admin.banners.noBanners')}
                </TableCell>
              </TableRow>
            ) : (
              images.map((banner) => (
                <TableRow key={banner.id}>
                  <TableCell>
                    <img
                      src={banner.image_url}
                      alt={banner.alt_text || 'Banner preview'}
                      style={{ height: '50px', maxWidth: '100px', objectFit: 'contain' }}
                    />
                  </TableCell>
                  <TableCell>{banner.image_url}</TableCell>
                  <TableCell>{banner.alt_text}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleDeleteClick(banner)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Banner Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.banners.addNew')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('admin.banners.imageUrl')}
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            margin="normal"
            helperText={t('admin.banners.urlHelperText')}
          />

          <TextField
            fullWidth
            label={t('admin.banners.altText')}
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            margin="normal"
          />

          {imageUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('admin.banners.preview')}:
              </Typography>
              <img
                src={imageUrl}
                alt={t('admin.banners.preview')}
                style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  setSnackbar({
                    open: true,
                    message: t('admin.banners.invalidUrl'),
                    severity: 'error',
                  });
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('admin.banners.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          {t('admin.banners.deleteConfirmMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
